import { Check, CirclePlus, Pencil, Trash2, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import {
  useNotificationChannels,
  useCreateNotificationChannel,
  useUpdateNotificationChannel,
  useDeleteNotificationChannel,
} from '../hooks/useNotifications.js';
import type { SlackChannel, SlackChannelInput } from '../types/notification.js';

/** @graph-connects none */
function emptyChannelInput(): SlackChannelInput {
  return { channelId: '', channelName: '', description: '', isActive: true };
}

/** @graph-connects none */
function channelToInput(ch: SlackChannel): SlackChannelInput {
  return {
    channelId: ch.channelId,
    channelName: ch.channelName,
    description: ch.description,
    isActive: ch.isActive,
  };
}

/** @graph-connects none */
const TD = 'border-b border-[var(--color-border)] p-2';
/** @graph-connects none */
const INPUT =
  'w-full rounded border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-2 py-1 text-xs';

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business チャンネル1行の表示・インライン編集・削除。
 * @graph-connects useUpdateNotificationChannel [calls] チャンネル更新
 * @graph-connects useDeleteNotificationChannel [calls] チャンネル削除
 */
function ChannelRow({ channel, canEdit }: { channel: SlackChannel; canEdit: boolean }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<SlackChannelInput>(channelToInput(channel));
  const updateMutation = useUpdateNotificationChannel();
  const deleteMutation = useDeleteNotificationChannel();

  if (editing) {
    return (
      <tr className="text-[var(--color-text-primary)]">
        <td className={TD}>
          <span className="text-xs font-mono">{form.channelId}</span>
        </td>
        <td className={TD}>
          <input
            className={INPUT}
            value={form.channelName}
            onChange={(e) => {
              setForm({ ...form, channelName: e.target.value });
            }}
          />
        </td>
        <td className={TD}>
          <input
            className={INPUT}
            value={form.description}
            onChange={(e) => {
              setForm({ ...form, description: e.target.value });
            }}
          />
        </td>
        <td className={`${TD} text-center`}>
          <input
            type="checkbox"
            checked={form.isActive}
            onChange={(e) => {
              setForm({ ...form, isActive: e.target.checked });
            }}
          />
        </td>
        <td className={TD}>
          <div className="flex gap-1">
            <button
              type="button"
              className="rounded p-1 text-[var(--color-accent-primary)] hover:bg-[var(--color-bg-elevated)]"
              disabled={updateMutation.isPending}
              onClick={() => {
                updateMutation.mutate(
                  { channelId: channel.channelId, input: form },
                  {
                    onSuccess: () => {
                      setEditing(false);
                    },
                  },
                );
              }}
            >
              <Check className="h-4 w-4" aria-hidden="true" />
            </button>
            <button
              type="button"
              className="rounded p-1 text-[var(--color-text-muted)] hover:bg-[var(--color-bg-elevated)]"
              onClick={() => {
                setEditing(false);
                setForm(channelToInput(channel));
              }}
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr className="text-[var(--color-text-primary)]">
      <td className={`${TD} text-xs font-mono`}>{channel.channelId}</td>
      <td className={`${TD} text-xs`}>{channel.channelName}</td>
      <td className={`${TD} text-xs text-[var(--color-text-secondary)]`}>
        {channel.description || '—'}
      </td>
      <td className={`${TD} text-center text-xs`}>
        {channel.isActive ? (
          <span className="text-[var(--color-accent-primary)]">{t('notification.enabled')}</span>
        ) : (
          <span className="text-[var(--color-text-muted)]">{t('notification.disabled')}</span>
        )}
      </td>
      <td className={TD}>
        {canEdit ? (
          <div className="flex gap-1">
            <button
              type="button"
              className="rounded p-1 text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-elevated)] hover:text-[var(--color-text-primary)]"
              onClick={() => {
                setEditing(true);
              }}
            >
              <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
            <button
              type="button"
              className="rounded p-1 text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-elevated)] hover:text-[var(--color-error)]"
              disabled={deleteMutation.isPending}
              onClick={() => {
                deleteMutation.mutate(channel.channelId);
              }}
            >
              <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          </div>
        ) : null}
      </td>
    </tr>
  );
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business 通知チャンネル一覧＋新規追加テーブル。
 * @graph-connects useNotificationChannels [calls] チャンネル一覧取得
 * @graph-connects useCreateNotificationChannel [calls] チャンネル新規作成
 * @graph-connects ChannelRow [calls] チャンネル行表示
 */
export function ChannelSection({ canEdit }: { canEdit: boolean }) {
  const { t } = useTranslation();
  const { data, isLoading, error } = useNotificationChannels();
  const createMutation = useCreateNotificationChannel();
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState<SlackChannelInput>(emptyChannelInput());

  if (isLoading)
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center py-8">
          <div className="loading-spinner" />
        </div>
      </Card>
    );
  if (error)
    return (
      <Card className="p-6 text-sm text-[var(--color-error)]">
        {t('notification.channel.fetchError')}
      </Card>
    );

  const channels = data?.channels ?? [];

  return (
    <Card className="overflow-x-auto p-5">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-bold text-[var(--color-text-primary)]">
          {t('notification.channel.title')}
        </h2>
        {canEdit && !adding ? (
          <button
            type="button"
            className="flex items-center gap-1 text-xs text-[var(--color-accent-primary)] hover:underline"
            onClick={() => {
              setAdding(true);
            }}
          >
            <CirclePlus className="h-3.5 w-3.5" aria-hidden="true" />
            {t('common.add')}
          </button>
        ) : null}
      </div>
      <table className="mt-3 w-full border-collapse text-xs">
        <thead>
          <tr className="text-[var(--color-text-secondary)]">
            <th className={`${TD} text-left`}>{t('notification.channel.idHeader')}</th>
            <th className={`${TD} text-left`}>{t('notification.channel.nameHeader')}</th>
            <th className={`${TD} text-left`}>{t('notification.channel.descriptionHeader')}</th>
            <th className={`${TD} text-center`}>{t('notification.stateHeader')}</th>
            <th className={`${TD} text-left w-20`} />
          </tr>
        </thead>
        <tbody>
          {channels.map((ch) => (
            <ChannelRow key={ch.channelId} channel={ch} canEdit={canEdit} />
          ))}
          {adding ? (
            <tr className="text-[var(--color-text-primary)]">
              <td className={TD}>
                <input
                  className={`${INPUT} font-mono`}
                  placeholder="C0XXXXXXXX"
                  value={form.channelId}
                  onChange={(e) => {
                    setForm({ ...form, channelId: e.target.value });
                  }}
                />
              </td>
              <td className={TD}>
                <input
                  className={INPUT}
                  placeholder={t('notification.channel.namePlaceholder')}
                  value={form.channelName}
                  onChange={(e) => {
                    setForm({ ...form, channelName: e.target.value });
                  }}
                />
              </td>
              <td className={TD}>
                <input
                  className={INPUT}
                  placeholder={t('notification.channel.descriptionPlaceholder')}
                  value={form.description}
                  onChange={(e) => {
                    setForm({ ...form, description: e.target.value });
                  }}
                />
              </td>
              <td className={`${TD} text-center`}>
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) => {
                    setForm({ ...form, isActive: e.target.checked });
                  }}
                />
              </td>
              <td className={TD}>
                <div className="flex gap-1">
                  <button
                    type="button"
                    className="rounded p-1 text-[var(--color-accent-primary)] hover:bg-[var(--color-bg-elevated)]"
                    disabled={createMutation.isPending || !form.channelId || !form.channelName}
                    onClick={() => {
                      createMutation.mutate(form, {
                        onSuccess: () => {
                          setAdding(false);
                          setForm(emptyChannelInput());
                        },
                      });
                    }}
                  >
                    <Check className="h-4 w-4" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className="rounded p-1 text-[var(--color-text-muted)] hover:bg-[var(--color-bg-elevated)]"
                    onClick={() => {
                      setAdding(false);
                      setForm(emptyChannelInput());
                    }}
                  >
                    <X className="h-4 w-4" aria-hidden="true" />
                  </button>
                </div>
              </td>
            </tr>
          ) : null}
          {channels.length === 0 && !adding ? (
            <tr>
              <td colSpan={5} className={`${TD} p-4 text-center text-[var(--color-text-muted)]`}>
                {t('notification.channel.noChannels')}
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </Card>
  );
}
